import React, {useCallback, useEffect, useState, useRef} from 'react';
import {View, FlatList, StyleSheet, RefreshControl, TouchableOpacity} from 'react-native';
import {Text, Chip, ActivityIndicator, Appbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {orderApi} from '../../services/api/orderApi';
import {colors, spacing} from '../../theme';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return {day: String(d.getDate()), month: MONTHS[d.getMonth()]};
};

const getAmountColor = (paymentStatus) => {
  if (paymentStatus === 'PAID') return colors.success;
  if (paymentStatus === 'UNPAID') return colors.error;
  if (paymentStatus === 'PARTIALLY_PAID') return colors.warning;
  return colors.text;
};

const StatusChip = ({status, t}) => (
  <Chip
    style={[styles.statusChip, {backgroundColor: (colors.status[status] || colors.info) + '20'}]}
    textStyle={styles.statusChipText}>
    <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{t(`order.status.${status}`)}</Text>
  </Chip>
);

const PAGE_SIZE = 20;

const CustomerOrdersScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId, customerName, summaryLabel, summaryAmount, summaryCount} = route.params;

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const loadingMore = useRef(false);

  const fetchPage = useCallback(async (pageNum, replace = false) => {
    try {
      const res = await orderApi.list({customerId, page: pageNum, size: PAGE_SIZE});
      const data = res.data.data;
      const content = data.content || [];
      setOrders(prev => replace ? content : [...prev, ...content]);
      setPage(data.page ?? pageNum);
      setTotalPages(data.totalPages ?? 1);
    } catch (e) {
      // silently fail on pagination, show empty on first load
    }
  }, [customerId]);

  useEffect(() => {
    setIsLoading(true);
    fetchPage(0, true).finally(() => setIsLoading(false));
  }, [fetchPage]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchPage(0, true);
    setRefreshing(false);
  }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore.current) return;
    const nextPage = page + 1;
    if (nextPage < totalPages) {
      loadingMore.current = true;
      fetchPage(nextPage).finally(() => {
        loadingMore.current = false;
      });
    }
  }, [page, totalPages, fetchPage]);

  const renderOrder = useCallback(({item}) => {
    const date = parseDate(item.deliveryDate);
    const amountColor = getAmountColor(item.paymentStatus);
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.row}
        onPress={() => navigation.navigate('Orders', {screen: 'OrdersTab', params: {screen: 'OrderView', params: {orderId: item.id}}})}>
        <View style={styles.dateCol}>
          {date ? (
            <>
              <Text style={styles.dateDay}>{date.day}</Text>
              <Text style={styles.dateMonth}>{date.month}</Text>
            </>
          ) : (
            <Text style={styles.datePlaceholder}>—</Text>
          )}
        </View>
        <View style={styles.detailsCol}>
          <Text style={styles.orderNumber}>{item.orderNumber}</Text>
          <Text style={styles.customerName} numberOfLines={1}>{item.customerName}</Text>
          <View style={styles.statusRow}>
            <StatusChip status={item.status} t={t} />
            {item.urgent && <Icon name="flag" size={18} color={colors.error} style={{marginLeft: 6}} />}
          </View>
        </View>
        <View style={styles.amountCol}>
          {item.balanceAmount != null && item.totalAmount != null &&
            Number(item.balanceAmount) !== Number(item.totalAmount) && (
            <Text style={styles.totalAmountStrike}>
              {'\u20B9'}{Number(item.totalAmount || 0).toLocaleString()}
            </Text>
          )}
          <Text style={[styles.balanceAmount, {color: amountColor}]}>
            {'\u20B9'}{Number(item.balanceAmount || 0).toLocaleString()}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }, [navigation, t]);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content
          title={`${customerName} — ${summaryLabel}`}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      {isLoading && orders.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={orders}
          renderItem={renderOrder}
          keyExtractor={item => String(item.id)}
          contentContainerStyle={styles.listContent}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.3}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text variant="titleMedium" style={styles.emptyTitle}>{t('common.noData')}</Text>
            </View>
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
          }
        />
      )}

      <View style={styles.footer}>
        <Text style={styles.footerLabel}>{summaryLabel}</Text>
        <Text style={styles.footerAmount}>
          {'\u20B9'}{Number(summaryAmount || 0).toLocaleString('en-IN')}
        </Text>
        <Text style={styles.footerCount}>
          {summaryCount} {summaryCount === 1 ? 'order' : 'orders'}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white, fontSize: 16},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {paddingBottom: 70},
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm + 3, paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  dateCol: {width: '20%', alignItems: 'center', justifyContent: 'center'},
  dateDay: {fontSize: 24, fontWeight: '400', color: colors.text, lineHeight: 28},
  dateMonth: {fontSize: 11, color: colors.textSecondary, fontWeight: '600', letterSpacing: 1},
  datePlaceholder: {fontSize: 24, color: colors.textLight},
  detailsCol: {flex: 1, paddingHorizontal: spacing.sm},
  orderNumber: {fontWeight: 'bold', color: colors.primary, fontSize: 14},
  customerName: {color: colors.text, fontSize: 13, marginVertical: 2},
  statusRow: {flexDirection: 'row', alignItems: 'center', marginTop: 2},
  statusChip: {paddingVertical: 0, height: 24},
  statusChipText: {fontSize: 14, color: colors.text},
  amountCol: {alignItems: 'flex-end', minWidth: 80},
  totalAmountStrike: {fontSize: 12, color: colors.textSecondary, textDecorationLine: 'line-through', marginBottom: 1},
  balanceAmount: {fontSize: 14, fontWeight: '700'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 2},
  emptyTitle: {color: colors.textSecondary},
  footer: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm + 2, paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1, borderTopColor: colors.divider,
  },
  footerLabel: {flex: 1, fontSize: 13, color: colors.textSecondary, fontWeight: '600'},
  footerAmount: {fontSize: 15, fontWeight: '700', color: colors.primary, marginRight: spacing.md},
  footerCount: {fontSize: 12, color: colors.textSecondary},
});

export default CustomerOrdersScreen;
